import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  getCourse, getCourseReviews, createCheckout, addReview, getMyEnrollments,
  validateCoupon, getActiveCampaigns, getAnnouncements,
} from "../services/api";
import api from "../services/api";
import useAuthStore from "../context/authStore";
import { Skeleton } from "../components/Skeleton";
import { useScrollReveal } from "../hooks/useScrollReveal";
import PdfAttachments from '../components/PdfAttachments';
import toast from "react-hot-toast";
import confetti from "canvas-confetti";
import Certificate from "../components/Certificate";
import "./CourseDetailPage.css";

import CampaignBanner from "../components/CampaignBanner";

/* ── Campaign bar (Condensed) ── */
function CampaignBar({ courseId, subject, price }) {
  const [campaign, setCampaign] = useState(null);
  useEffect(() => {
    getActiveCampaigns().then(r => {
      const m = (r.data || []).find(c => {
        if (c.applicableCourses?.length > 0 && !c.applicableCourses.includes(courseId)) return false;
        if (c.applicableCategories?.length > 0 && !c.applicableCategories.includes(subject)) return false;
        return true;
      });
      setCampaign(m || null);
    }).catch(() => {});
  }, [courseId, subject]);
  if (!campaign || price <= 0) return null;

  return (
    <div className="cd-mini-campaign-bar">
      <div className="cmc-inner">
        <span className="cmc-urgent">عرض خاص 🏷️</span>
        <span className="cmc-text">
          وفر <strong>{campaign.discountType === 'percentage' ? `${campaign.discountValue}%` : `${campaign.discountValue} ج.م`}</strong> على هذا الكورس!
        </span>
      </div>
    </div>
  );
}

/* ── PDF list for enrolled students on course page ── */
function CourseAnnouncements({ courseId }) {
  const [announcements, setAnnouncements] = useState([]);
  useEffect(() => {
    if (courseId) {
      getAnnouncements(courseId)
        .then(r => setAnnouncements(r.data || []))
        .catch(() => {});
    }
  }, [courseId]);

  if (!announcements.length) return null;

  return (
    <div className="cd-section">
      <h2 className="cd-section-title">📢 تنبيهات الكورس</h2>
      <div className="announcements-list">
        {announcements.map(a => (
          <div key={a.id} className="announcement-item card">
            <div className="announcement-header">
              <span className="announcement-title">{a.title}</span>
              <span className="announcement-date">
                {new Date(a.createdAt).toLocaleDateString('ar-EG')}
              </span>
            </div>
            <p className="announcement-content">{a.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CourseDetailPage() {
  const { id }          = useParams(); // This is now the course slug or ID
  const [searchParams]  = useSearchParams();
  const navigate        = useNavigate();
  const { user, userDoc } = useAuthStore();

  const [course,       setCourse]       = useState(null);
  const [activeCampaign, setCampaign]   = useState(null);
  const [reviews,      setReviews]      = useState([]);
  const [enrollment,   setEnrollment]   = useState(null);
  const [enState,      setEnState]      = useState(null); // paidLessons for perLesson mode
  const [loading,      setLoading]      = useState(true);
  const [showPayModal, setPayModal]     = useState(false);
  const [payMethod,    setPayMethod]    = useState("card");
  const [phoneNumber,  setPhone]        = useState("");
  const [enrolling,    setEnrolling]    = useState(false);
  const [iframeUrl,    setIframeUrl]    = useState(null);
  const [reviewText,   setReviewText]   = useState("");
  const [reviewRating, setRating]       = useState(5);
  const [couponInput,  setCouponInput]  = useState('');
  const [couponResult, setCouponResult] = useState(null);
  const [useCredits,   setUseCredits]   = useState(false);
  const [validating,   setValidating]   = useState(false);
  const [liveStudents, setLiveStudents] = useState(0);
  const [weeklyJoiners, setWeeklyJoiners] = useState(5);
  const [viewCert,     setViewCert]     = useState(null);

  // per-lesson pay modal
  const [lessonPayIdx,  setLessonPayIdx]  = useState(null); // which lesson we're paying for
  const [lessonPayMethod, setLessonPayMethod] = useState('card');
  const [lessonPhone,   setLessonPhone]   = useState('');
  const [lessonPaying,  setLessonPaying]  = useState(false);

  const pageRef = useScrollReveal();

  useEffect(() => {
    if (course?.title) {
      document.title = `${course.title} | مدرسك`;
    }
    return () => { document.title = 'مدرسك'; };
  }, [course?.title]);

  useEffect(() => {
    // Simulated Live Students
    setLiveStudents(Math.floor(Math.random() * 15) + 3);
    const interval = setInterval(() => {
      setLiveStudents(prev => {
        const change = Math.random() > 0.5 ? 1 : -1;
        return Math.max(1, prev + change);
      });
    }, 10000);

    setLoading(true);
    Promise.all([
      getCourse(id),
      getCourseReviews(id),
      getActiveCampaigns(),
      api.get(`/analytics/course-joiners/${id}`).catch(() => ({ data: { count: 5 } }))
    ])
      .then(([cr, rr, acr, jr]) => {
        setWeeklyJoiners(jr.data.count);
        setCourse(cr.data);
        setReviews(rr.data || []);
        // Find best applicable campaign
        const m = (acr.data || []).find(c => {
          if (c.applicableCourses?.length > 0 && !c.applicableCourses.includes(id)) return false;
          if (c.applicableCategories?.length > 0 && !c.applicableCategories.includes(cr.data.subject)) return false;
          return true;
        });
        setCampaign(m || null);
      })
      .catch(() => toast.error("حدث خطأ في تحميل الكورس"))
      .finally(() => setLoading(false));

    if (user && course) {
      getMyEnrollments().then(r => {
        const en = (r.data || []).find(e => e.courseId === course.id) || null;
        setEnrollment(en);
      }).catch(() => {});
      // Get per-lesson state
      api.get(`/lessons/${course.id}/enrollment-state`).then(r => setEnState(r.data)).catch(() => {});
    }

    const payment = searchParams.get("payment");
    if (payment === "success") {
      toast.success("تم الدفع بنجاح! 🎉");
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: ["#1a9bd5", "#0dd4b8", "#f59e0b"]
      });
      if (user) setTimeout(() => {
        getMyEnrollments().then(r => setEnrollment((r.data || []).find(e => e.courseId === id) || null)).catch(() => {});
        api.get(`/lessons/${id}/enrollment-state`).then(r => setEnState(r.data)).catch(() => {});
      }, 2000);
    }
    if (payment === "failed") toast.error("فشل الدفع — حاول مرة أخرى");
  }, [id, user]); // eslint-disable-line

  const isEnrolled  = !!enrollment;
  const isTeacher   = userDoc?.role === "teacher" && course?.teacherId === user?.uid;
  const isAdmin     = userDoc?.role === "admin";
  const isPerLesson = course?.paymentType === 'perLesson';
  const canWatch    = isEnrolled || isTeacher || isAdmin;
  const actualId    = course?.id;

  /* For per-lesson: is this specific lesson accessible? */
  const lessonAccessible = (idx) => {
    if (!course) return false;
    if (isTeacher || isAdmin) return true;
    const l = course.lessons?.[idx];
    if (l?.isFree || idx === 0) return true;
    if (!isPerLesson) return canWatch;
    return (enState?.paidLessons || []).includes(idx);
  };

  /* Navigate to lesson player */
  const openLesson = (idx) => {
    if (!user) {
      toast.error("سجّل دخولك أولاً لمشاهدة المحتوى");
      navigate(`/login?redirect=/courses/${course?.slug || id}`);
      return;
    }
    if (lessonAccessible(idx)) {
      navigate(`/courses/${course?.slug || id}/lessons/${idx}`);
    } else if (isPerLesson) {
      setLessonPayIdx(idx);
    } else {
      toast.error("اشترك في الكورس لمشاهدة هذا الدرس");
    }
  };

  /* Full-course coupon validate */
  const handleValidateCoupon = async () => {
    if (!couponInput.trim()) return;
    setValidating(true);
    try {
      const r = await validateCoupon(couponInput.trim(), id, parseFloat(course.price));
      setCouponResult(r.data);
      toast.success(`✅ خصم ${r.data.discount} ج.م`);
    } catch (err) { setCouponResult(null); toast.error(err.response?.data?.error || 'كوبون غير صحيح'); }
    setValidating(false);
  };

  /* Full-course checkout */
  const handleEnrollClick = () => {
    if (!user) {
      toast.error("سجّل دخولك أولاً للاشتراك");
      navigate(`/login?redirect=/courses/${course?.slug || id}`);
      return;
    }
    if (userDoc?.role !== "student") return toast.error("فقط الطلاب يمكنهم الاشتراك");
    if (isEnrolled) return navigate(`/courses/${course?.slug || id}/lessons/0`);
    const price = parseFloat(course.price) || 0;
    if (price === 0) { handleFreeCourse(); return; }
    setPayModal(true);
  };

  const handleFreeCourse = async () => {
    setEnrolling(true);
    try {
      const r = await createCheckout(actualId, "free");
      if (r.data.enrolled) { setEnrollment({ courseId: actualId }); toast.success("🎉 تم الاشتراك!"); }
    } catch (err) { toast.error(err.response?.data?.error || "حدث خطأ"); }
    setEnrolling(false);
  };

  const handlePay = async () => {
    if (payMethod === "wallet" && !phoneNumber) return toast.error("أدخل رقم الهاتف");
    setEnrolling(true);
    try {
      const r = await api.post('/payments/checkout', {
        courseId: actualId, method: payMethod,
        phoneNumber: phoneNumber || undefined,
        couponCode: couponResult ? couponInput.trim() : undefined,
        useCredits,
      });
      if (r.data.enrolled) { setEnrollment({ courseId: id }); toast.success("🎉 تم الاشتراك!"); setPayModal(false); }
      else if (r.data.method === "wallet" && r.data.redirectUrl) window.location.href = r.data.redirectUrl;
      else if (r.data.method === "card"   && r.data.iframeUrl)   setIframeUrl(r.data.iframeUrl);
    } catch (err) { toast.error(err.response?.data?.error || "حدث خطأ"); }
    setEnrolling(false);
  };

  /* Per-lesson checkout */
  const handleLessonPay = async () => {
    if (lessonPayMethod === 'wallet' && !lessonPhone) return toast.error('أدخل رقم المحفظة');
    setLessonPaying(true);
    try {
      const r = await api.post('/payments/checkout', {
        courseId: actualId, method: lessonPayMethod,
        phoneNumber: lessonPhone || undefined,
        lessonIndex: lessonPayIdx,
      });
      if (r.data.enrolled) { toast.success('🎉 تم فتح الدرس!'); setLessonPayIdx(null); navigate(`/courses/${course?.slug || id}/lessons/${lessonPayIdx}`); }
      else if (r.data.method === 'wallet' && r.data.redirectUrl) window.location.href = r.data.redirectUrl;
      else if (r.data.method === 'card'   && r.data.iframeUrl)   window.open(r.data.iframeUrl, '_blank');
    } catch (err) { toast.error(err.response?.data?.error || 'حدث خطأ'); }
    setLessonPaying(false);
  };

  const handleReview = async (e) => {
    e.preventDefault();
    try {
      await addReview(actualId, { rating: reviewRating, comment: reviewText });
      toast.success("تم إضافة تقييمك!");
      const rr = await getCourseReviews(actualId); setReviews(rr.data || []); setReviewText("");
    } catch (err) { toast.error(err.response?.data?.error || "حدث خطأ"); }
  };

  const stars = r => "★".repeat(Math.round(r || 0)) + "☆".repeat(5 - Math.round(r || 0));

  if (loading) return (
    <div className="course-detail">
      <div className="cd-banner-wrap">
        <Skeleton width="100%" height="240px" borderRadius="0" />
      </div>
      <div className="cd-hero">
        <div className="cd-hero-inner">
          <div className="cd-hero-text" style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <Skeleton width="60px" height="24px" borderRadius="20px" />
              <Skeleton width="60px" height="24px" borderRadius="20px" />
            </div>
            <Skeleton width="70%" height="40px" className="mb-12" />
            <Skeleton width="90%" height="20px" className="mb-8" />
            <Skeleton width="85%" height="20px" className="mb-12" />
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Skeleton width="32px" height="32px" borderRadius="50%" />
              <Skeleton width="120px" height="18px" />
            </div>
          </div>
          <div className="cd-enroll-card">
            <Skeleton width="100%" height="180px" borderRadius="12px 12px 0 0" />
            <div style={{ padding: '20px' }}>
              <Skeleton width="100%" height="48px" borderRadius="8px" className="mb-12" />
              <Skeleton width="100%" height="100px" borderRadius="8px" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  if (!course) return (
    <div className="cd-notfound"><h2>الكورس غير موجود</h2>
      <Link to="/courses" className="btn btn-primary" style={{ marginTop: 16 }}>تصفح الكورسات</Link></div>
  );

  const basePrice = parseFloat(course.price) || 0;
  let campaignDiscount = 0;
  if (activeCampaign && basePrice > 0) {
    campaignDiscount = activeCampaign.discountType === 'percentage'
      ? basePrice * (activeCampaign.discountValue / 100)
      : activeCampaign.discountValue;
  }
  const priceAfterCampaign = Math.max(0, basePrice - campaignDiscount);

  const finalPrice = couponResult ? couponResult.finalPrice : priceAfterCampaign;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="course-detail page-enter"
      ref={pageRef}
    >

      {/* Campaign bar */}
      {!isEnrolled && basePrice > 0 && (
        <CampaignBar courseId={course.id} subject={course.subject} price={basePrice} />
      )}

      {/* Banner */}
      <div className="cd-banner-wrap">
        {course.banner ? <img src={course.banner} alt="" className="cd-banner-img" /> : <div className="cd-banner-ph" />}
        <div className="cd-banner-overlay" />
      </div>

      {/* Hero */}
      <div className="cd-hero">
        <div className="cd-hero-inner">
          <div className="cd-hero-text">
            <div className="cd-badges">
              {course.subject && <span className="badge badge-blue">{course.subject}</span>}
              {course.isLive  && <span className="badge" style={{ background:'rgba(254,226,226,.9)', color:'#991b1b' }}>🔴 لايف</span>}
              {isPerLesson    && <span className="badge" style={{ background:'rgba(124,58,237,.12)', color:'#7c3aed' }}>🎟️ ادفع درساً بدرس</span>}
              {isEnrolled     && <span className="badge badge-green">✅ مشترك</span>}
              <span className="badge badge-live-count">
                <span className="live-dot" /> {liveStudents} طالب يدرسون الآن
              </span>
            </div>
            <h1 data-reveal="up">{course.title}</h1>
            {course.description && <p className="cd-desc" data-reveal="up" data-delay="60">{course.description}</p>}
            <div className="cd-meta" data-reveal="up" data-delay="100">
              {course.teacherAvatar
                ? <img src={course.teacherAvatar} alt="" className="cd-teacher-avatar" />
                : <span>👤</span>}
              <Link to={`/teachers/${course.teacherUsername || course.teacherId}`} className="cd-teacher-link">{course.teacherName}</Link>

              <div className="cd-share-viral">
                <button
                  className="btn btn-outline btn-xs"
                  style={{ fontSize: 10, padding: '4px 10px', marginRight: 8, display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success('تم نسخ رابط الكورس! شاركه مع زملائك 🔗');
                  }}
                >
                  🔗 نسخ الرابط
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`يا جماعة شوفوا الكورس ده بجد جامد جداً: "${course.title}" على منصة مدرسك 📚\n\n${window.location.href}`)}`}
                  target="_blank" rel="noreferrer"
                  className="btn-viral-share btn-wa"
                  title="شارك على واتساب"
                >
                  <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WA" style={{ width: 14 }} />
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank" rel="noreferrer"
                  className="btn-viral-share btn-fb"
                  title="شارك على فيسبوك"
                >
                  <img src="https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg" alt="FB" style={{ width: 14 }} />
                </a>
              </div>
              {course.rating > 0 && (<>
                <span className="cd-meta-sep">·</span>
                <span className="stars">{stars(course.rating)}</span>
                <span>({course.totalReviews || 0})</span>
              </>)}
              <span className="cd-meta-sep">·</span><span>{course.enrolledCount || 0} طالب</span>
              <span className="cd-meta-sep">·</span><span>{course.lessons?.length || 0} درس</span>
            </div>
          </div>

          {/* Enroll card */}
          <div className="cd-enroll-card" data-reveal="left" data-delay="80">
            {course.thumbnail
              ? <img src={course.thumbnail} alt={course.title} className="cd-thumb" />
              : <div className="cd-thumb-ph">📚</div>}
            <div className="cd-enroll-body">
              {canWatch ? (
                <>
                  <div className="enrolled-badge">✅ أنت مشترك في هذا الكورس</div>
                  {enrollment?.progress > 0 && (
                    <div style={{ margin:"10px 0" }}>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                        <span>تقدمك</span><span style={{ fontWeight:700, color:'var(--blue)' }}>{enrollment.progress}%</span>
                      </div>
                      <div className="progress-bar"><div className="progress-fill" style={{ width:`${enrollment.progress}%` }} /></div>
                    </div>
                  )}
                  <button className="btn btn-primary btn-lg" style={{ width:"100%", justifyContent:"center" }}
                    onClick={() => navigate(`/courses/${course?.slug || id}/lessons/0`)}>
                    ▶ متابعة الدراسة
                  </button>
                  {course.whatsappGroupLink && (
                    <a
                      href={course.whatsappGroupLink}
                      target="_blank" rel="noreferrer"
                      className="btn btn-outline btn-lg"
                      style={{ width:"100%", justifyContent:"center", marginTop:10, color:'#25D366', borderColor:'#25D366', fontWeight:800 }}
                    >
                      💬 جروب واتساب الطلاب
                    </a>
                  )}
                  {enrollment?.progress >= 100 && course.isCertificateEnabled && (
                    <button
                      className="btn btn-outline btn-lg"
                      style={{ width:"100%", justifyContent:"center", marginTop:10, border:'2px solid var(--blue)', fontWeight:800 }}
                      onClick={() => {
                        api.get(`/enrollments/certificate/${actualId}`)
                          .then(r => setViewCert(r.data))
                          .catch((err) => {
                            if (err.response?.status === 403 && err.response?.data?.missingQuizCount) {
                              toast.error(`تحتاج لاجتياز ${err.response.data.missingQuizCount} اختبار متبقي أولاً 📝`);
                            } else {
                              toast.error(err.response?.data?.error || 'الشهادة قيد التجهيز، جرب لاحقاً');
                            }
                          });
                      }}
                    >
                      🎓 عرض الشهادة
                    </button>
                  )}
                </>
              ) : isPerLesson ? (
                <>
                  <div className="cd-perlession-badge">🎟️ ادفع درساً بدرس</div>
                  <p style={{ fontSize:13, color:'var(--text-muted)', marginBottom:14 }}>
                    الدرس الأول مجاني — تدفع فقط للدروس التي تريدها
                  </p>
                  <button className="btn btn-primary btn-lg" style={{ width:"100%", justifyContent:"center" }}
                    onClick={() => navigate(`/courses/${course?.slug || id}/lessons/0`)}>
                    ▶ ابدأ بالدرس المجاني
                  </button>
                </>
              ) : (
                <>
                  <div className="cd-price">
                    {basePrice === 0 ? (
                      <span className="price-free">مجاني 🎁</span>
                    ) : (
                      <div>
                        {(couponResult || campaignDiscount > 0) && (
                          <span style={{ textDecoration: 'line-through', fontSize: 16, color: 'var(--text-muted)', marginLeft: 8 }}>
                            {basePrice} ج.م
                          </span>
                        )}
                        <span className="price-paid">{finalPrice} ج.م</span>
                        {campaignDiscount > 0 && !couponResult && (
                          <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700, marginTop: 2 }}>
                            🔥 عرض {activeCampaign.title}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button className="btn btn-primary btn-lg" style={{ width: "100%", justifyContent: "center" }}
                    onClick={handleEnrollClick} disabled={enrolling}>
                    {enrolling ? <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> : `🎓 اشترك الآن — ${finalPrice} ج.م`}
                  </button>

                  {/* Social Proof Counter */}
                  {(course.enrolledThisWeek > 0 || weeklyJoiners > 0) && (
                    <div style={{ marginTop: 12, textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(245,158,11,0.1)', padding: '6px 12px', borderRadius: 20, border: '1px solid rgba(245,158,11,0.2)' }}>
                        <span style={{ fontSize: 16 }}>🔥</span>
                        <span style={{ fontSize: 13, color: '#b45309', fontWeight: 700 }}>
                          انضم {course.enrolledThisWeek || weeklyJoiners} طالباً لهذا الكورس هذا الأسبوع
                        </span>
                      </div>
                    </div>
                  )}

                  <p className="enroll-note" style={{ marginTop: 12 }}>أول درس مجاناً · ضمان استرداد 7 أيام</p>
                  <div className="cd-includes">
                    <div>✅ وصول كامل للمحتوى</div>
                    <div>✅ شهادة إتمام الكورس</div>
                    <div>✅ دعم مباشر من المدرّس</div>
                    {course.isLive && <div>✅ حصص لايف تفاعلية</div>}
                  </div>
                  {basePrice > 0 && (
                    <div className="payment-methods-preview">
                      <span>طرق الدفع:</span>
                      <div className="pm-icons">
                        <span className="pm-icon">💳 فيزا</span>
                        <span className="pm-icon">📱 فودافون كاش</span>
                        <span className="pm-icon">🟠 اورنج كاش</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="cd-body">
        <div className="cd-main">
          {/* Lessons list */}
          <div className="cd-section">
            <h2 className="cd-section-title">
              📖 محتوى الكورس
              <span className="cd-section-count">{course.lessons?.length || 0} درس</span>
            </h2>
            {isPerLesson && (
              <div className="cd-perlession-info">
                🎟️ هذا الكورس يعمل بنظام <strong>ادفع درساً بدرس</strong> — الدرس الأول مجاني ثم تدفع لكل درس بشكل منفصل.
              </div>
            )}
            <div className="lessons-list">
              {(course.lessons || []).map((lesson, i) => {
                const accessible = lessonAccessible(i);
                const lessonPrice = lesson.price || (isPerLesson ? course.price : 0);
                return (
                  <div key={lesson.id || i} className="lesson-item" data-reveal="up" data-delay={`${i * 35}`}>
                    <button
                      className="lesson-header lesson-header-clickable"
                      onClick={() => openLesson(i)}
                    >
                      {/* Left: num / status */}
                      <span className={`lesson-num ${accessible ? 'lesson-num-open' : ''}`}>
                        {accessible ? (i === 0 || lesson.isFree ? '▶' : '▶') : '🔒'}
                      </span>

                      {/* Title */}
                      <span className="lesson-title">{lesson.title}</span>

                      {/* Right meta */}
                      <span className="lesson-meta">
                        {lesson.duration && <span>{lesson.duration}</span>}
                        {lesson.isFree || i === 0
                          ? <span className="badge badge-green" style={{ fontSize:10 }}>مجاني</span>
                          : isPerLesson && !accessible
                            ? <span className="lp-lesson-price-badge">{lessonPrice} ج.م</span>
                            : accessible
                              ? <span className="badge badge-blue" style={{ fontSize:10 }}>🔓</span>
                              : <span className="lesson-lock">🔒</span>
                        }
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Announcements */}
          <CourseAnnouncements courseId={actualId} />

          {/* PDF Attachments for selected lesson if any - if student is teacher or enrolled */}
          {canWatch && (
            <div className="cd-section">
              <PdfAttachments
                courseId={actualId}
                lessonIndex={0}
                isOwner={isTeacher || isAdmin}
              />
            </div>
          )}

          {/* Key Points & Summary for first lesson (since it's free/preview) */}
          {course.lessons?.[0] && (
            <div className="cd-section">
              <h2 className="cd-section-title">💡 ماذا ستتعلم في أول درس؟</h2>
              <div className="card" style={{ padding: 20 }}>
                {course.lessons[0].keyPoints?.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ marginBottom: 10, fontSize: 14 }}>النقاط الرئيسية:</h4>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      {course.lessons[0].keyPoints.map((kp, i) => (
                        <li key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: 13 }}>
                          <span style={{ color: 'var(--blue)' }}>✦</span>
                          {kp}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {course.lessons[0].summary && (
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                    <h4 style={{ marginBottom: 8, fontSize: 14 }}>ملخص الدرس:</h4>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>{course.lessons[0].summary}</p>
                  </div>
                )}
                {!course.lessons[0].keyPoints?.length && !course.lessons[0].summary && (
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>لا توجد تفاصيل إضافية متاحة لهذا الدرس حالياً.</p>
                )}
              </div>
            </div>
          )}

          {/* Reviews */}
          <div className="cd-section">
            <h2 className="cd-section-title">⭐ تقييمات الطلاب</h2>
            {isEnrolled && (
              <form className="review-form" onSubmit={handleReview}>
                <div className="form-group">
                  <label className="form-label">تقييمك للكورس</label>
                  <div className="star-picker">
                    {[5,4,3,2,1].map(s => (
                      <button key={s} type="button" onClick={() => setRating(s)}
                        style={{ fontSize:26, background:"none", border:"none", cursor:"pointer", color: s <= reviewRating ? "#f59e0b" : "#d1d5db" }}>★</button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <textarea className="form-input" rows={3} placeholder="اكتب تعليقك..."
                    value={reviewText} onChange={e => setReviewText(e.target.value)} />
                </div>
                <button type="submit" className="btn btn-primary btn-sm">إرسال التقييم</button>
              </form>
            )}
            {reviews.length === 0
              ? <p style={{ color:"var(--text-muted)", fontSize:14, padding:"20px 0" }}>لا توجد تقييمات بعد</p>
              : <div className="reviews-list">
                {reviews.map(r => (
                  <div key={r.id} className="review-item">
                    <div className="review-header">
                      {r.studentAvatar && <img src={r.studentAvatar} alt="" style={{ width:32, height:32, borderRadius:"50%", objectFit:"cover" }} />}
                      <div><strong style={{ fontSize:13 }}>{r.studentName}</strong>
                        <div style={{ color:"#f59e0b", fontSize:13 }}>{"★".repeat(r.rating)}{"☆".repeat(5-r.rating)}</div>
                      </div>
                    </div>
                    {r.comment && <p className="review-comment">{r.comment}</p>}
                  </div>
                ))}
              </div>
            }
          </div>

          {course.teacherName && (
            <div className="cd-section">
              <h2 className="cd-section-title">👨‍🏫 عن المدرّس</h2>
              <div className="teacher-info">
                {course.teacherAvatar && <img src={course.teacherAvatar} alt="" className="teacher-info-avatar" />}
                <div>
                  <div className="teacher-info-name">{course.teacherName}</div>
                  <Link to={`/teachers/${course.teacherUsername || course.teacherId}`} className="btn btn-outline btn-sm">عرض الملف الشخصي ←</Link>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="cd-aside" />
      </div>

      {/* ── Full-course pay modal ── */}
      {showPayModal && !iframeUrl && (
        <div className="pay-modal-overlay" onClick={() => setPayModal(false)}>
          <div className="pay-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
              <div className="pay-modal-title">إتمام الاشتراك</div>
              <button style={{ background:"none", border:"none", fontSize:20, cursor:"pointer" }} onClick={() => setPayModal(false)}>✕</button>
            </div>
            <p className="pay-modal-sub">{course.title}</p>

            {/* Price summary */}
            <div className="cd-price-summary">
              <div className="cd-ps-row"><span>السعر الأصلي</span><span>{basePrice} ج.م</span></div>
              {campaignDiscount > 0 && (
                <div className="cd-ps-row cd-ps-discount">
                  <span>خصم الحملة ({activeCampaign.title})</span>
                  <span>− {campaignDiscount} ج.م</span>
                </div>
              )}
              {couponResult && <div className="cd-ps-row cd-ps-discount"><span>خصم الكوبون ({couponResult.code})</span><span>− {couponResult.discount} ج.م</span></div>}
              {useCredits && userDoc?.credits > 0 && course?.allowCredits && (
                <div className="cd-ps-row cd-ps-discount">
                  <span>استخدام رصيد الهدايا</span>
                  <span>− {Math.min(finalPrice, userDoc.credits)} ج.م</span>
                </div>
              )}
              <div className="cd-ps-row cd-ps-total">
                <span>الإجمالي</span>
                <span>{Math.max(0, finalPrice - (useCredits ? userDoc?.credits || 0 : 0))} ج.م</span>
              </div>
            </div>

            {/* Gift Credits Toggle */}
            {userDoc?.credits > 0 && course?.allowCredits && (
              <div style={{ marginBottom: 16, padding: 12, background: 'rgba(13,212,184,0.05)', border: '1px solid var(--teal)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 13 }}>
                  <span style={{ display: 'block', fontWeight: 700, color: 'var(--teal)' }}>🎁 متاح لك {userDoc.credits} ج.م رصيد هدايا</span>
                  <span style={{ fontSize: 11, opacity: 0.8 }}>هل تريد استخدامهم لخصم السعر؟ (متاح لهذا الكورس)</span>
                </div>
                <label className="switch" style={{ position: 'relative', display: 'inline-block', width: 44, height: 24 }}>
                  <input type="checkbox" checked={useCredits} onChange={e => setUseCredits(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
                  <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, background: useCredits ? 'var(--teal)' : '#ccc', transition: '0.3s', borderRadius: 24 }}>
                    <span style={{ position: 'absolute', height: 18, width: 18, left: useCredits ? 22 : 3, bottom: 3, background: '#fff', transition: '0.3s', borderRadius: '50%' }} />
                  </span>
                </label>
              </div>
            )}

            {/* Coupon */}
            {couponResult ? (
              <div className="cd-coupon-applied">
                <span>🎉 كوبون {couponResult.code} — خصم {couponResult.discount} ج.م</span>
                <button onClick={() => { setCouponResult(null); setCouponInput(''); }}>✕</button>
              </div>
            ) : (
              <div className="cd-coupon-row">
                <input className="form-input" placeholder="كود الخصم (اختياري)" value={couponInput}
                  onChange={e => setCouponInput(e.target.value.toUpperCase())} />
                <button className="btn btn-outline btn-sm" onClick={handleValidateCoupon}
                  disabled={validating || !couponInput.trim()}>{validating ? '...' : 'تطبيق'}</button>
              </div>
            )}

            <div className="pay-method-row">
              {["card","wallet"].map(m => (
                <button key={m} className={`pay-method-btn ${payMethod === m ? "active" : ""}`} onClick={() => setPayMethod(m)}>
                  {m === "card" ? "💳 بطاقة بنكية" : "📱 محفظة إلكترونية"}
                </button>
              ))}
            </div>
            {payMethod === "wallet" && (
              <div className="form-group" style={{ marginBottom:16 }}>
                <label className="form-label">رقم المحفظة</label>
                <input className="form-input" placeholder="01XXXXXXXXX" value={phoneNumber}
                  onChange={e => setPhone(e.target.value)} dir="ltr" />
              </div>
            )}
            <button className="btn btn-primary btn-lg" style={{ width:"100%", justifyContent:"center" }}
              onClick={handlePay} disabled={enrolling}>
              {enrolling ? <div className="spinner" style={{width:18,height:18,borderWidth:2}}/> : `ادفع ${finalPrice} ج.م`}
            </button>
          </div>
        </div>
      )}

      {iframeUrl && (
        <div className="pay-modal-overlay">
          <div className="pay-modal" style={{ maxWidth:640, padding:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div className="pay-modal-title">إتمام الدفع</div>
              <button style={{ background:"none", border:"none", fontSize:20, cursor:"pointer" }} onClick={() => setIframeUrl(null)}>✕</button>
            </div>
            <iframe src={iframeUrl} className="pay-iframe" title="Paymob Payment" />
          </div>
        </div>
      )}

      {viewCert && <Certificate certificate={viewCert} onClose={() => setViewCert(null)} />}

      {/* ── Per-lesson pay modal ── */}
      {lessonPayIdx !== null && (
        <div className="pay-modal-overlay" onClick={() => setLessonPayIdx(null)}>
          <div className="pay-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
              <div className="pay-modal-title">فتح الدرس</div>
              <button style={{ background:"none", border:"none", fontSize:20, cursor:"pointer" }} onClick={() => setLessonPayIdx(null)}>✕</button>
            </div>
            <p className="pay-modal-sub">{course.lessons?.[lessonPayIdx]?.title}</p>
            <div style={{ fontSize:28, fontWeight:800, color:'var(--blue)', marginBottom:16 }}>
              {course.lessons?.[lessonPayIdx]?.price || course.price} ج.م
            </div>
            <div className="pay-method-row">
              {["card","wallet"].map(m => (
                <button key={m} className={`pay-method-btn ${lessonPayMethod === m ? "active" : ""}`} onClick={() => setLessonPayMethod(m)}>
                  {m === "card" ? "💳 بطاقة بنكية" : "📱 محفظة إلكترونية"}
                </button>
              ))}
            </div>
            {lessonPayMethod === "wallet" && (
              <div className="form-group" style={{ marginBottom:16 }}>
                <label className="form-label">رقم المحفظة</label>
                <input className="form-input" placeholder="01XXXXXXXXX" value={lessonPhone}
                  onChange={e => setLessonPhone(e.target.value)} dir="ltr" />
              </div>
            )}
            <button className="btn btn-primary btn-lg" style={{ width:"100%", justifyContent:"center" }}
              onClick={handleLessonPay} disabled={lessonPaying}>
              {lessonPaying ? <div className="spinner" style={{width:18,height:18,borderWidth:2}}/> : `ادفع وشاهد الدرس`}
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
}
